import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import {
	edit_05,
	searchSmLine,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import type { UIEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import { TraxCard } from "~/shared/components/trax-card/trax-card.tsx";
import type { TraxUser } from "~/shared/schemas/trax-user-schema.ts";
import { getRoleId } from "~/shared/schemas/trax-user-schema.ts";

import styles from "./mobile-user-management.module.css";

type MobileUserManagementProps = {
	currentUserRole: string;
	loading: boolean;
	users: TraxUser[];
	onEdit: (user: TraxUser) => void;
	onDelete: (user: TraxUser) => void;
};

const LOADING_CARD_COUNT = 6;
const PAGE_SIZE = 25;
const SCROLL_THRESHOLD = 2;

const LoadingUserCard = () => (
	<div className={styles.userCard} data-testid={"user-card-skeleton"}>
		<div className={`${styles.row} ${styles.cardHeader}`}>
			<div>Email</div>
			<div>Permission Level</div>
		</div>
		<div className={`${styles.row} ${styles.cardContent}`}>
			<span className={styles.skeletonText}>
				<Skeleton animation={{ type: "wave" }} shape={"text"} />
			</span>
			<span className={styles.skeletonText}>
				<Skeleton animation={{ type: "wave" }} shape={"text"} />
			</span>
		</div>
		<div className={styles.actions}>
			<span className={styles.skeletonAction}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
			<span className={styles.skeletonAction}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
		</div>
	</div>
);

export const MobileUserManagement = ({
	currentUserRole,
	loading,
	users,
	onEdit,
	onDelete,
}: MobileUserManagementProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const filteredUsers = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return users;
		return users.filter((user) =>
			[user.firstName, user.lastName, user.email, user.role]
				.filter(Boolean)
				.some((field) => field?.toLowerCase().includes(term)),
		);
	}, [searchTerm, users]);

	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [searchTerm]);

	const visibleUsers = useMemo(
		() => filteredUsers.slice(0, Math.min(visibleCount, filteredUsers.length)),
		[filteredUsers, visibleCount],
	);

	const handleSearchChange = (event: InputChangeEvent) => {
		setSearchTerm(String(event.value ?? ""));
	};

	const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
		if (visibleCount >= filteredUsers.length) return;
		const target = event.currentTarget;
		const distanceFromBottom =
			target.scrollHeight - (target.scrollTop + target.clientHeight);
		if (distanceFromBottom <= SCROLL_THRESHOLD) {
			setVisibleCount((count) =>
				Math.min(count + PAGE_SIZE, filteredUsers.length),
			);
		}
	};

	return (
		<TraxCard
			className={styles.managementCard}
			contentClassName={styles.managementCardContent}
			title={"User Management"}
		>
			<div className={styles.searchWrapper}>
				<Input
					aria-label={"Search users"}
					className={styles.searchInput}
					data-testid={"user-search-input"}
					disabled={loading}
					onChange={handleSearchChange}
					placeholder={"Search users..."}
					value={searchTerm}
				/>
				<SvgIcon className={styles.searchIcon} icon={searchSmLine} />
			</div>
			<div
				aria-busy={loading}
				className={styles.listScrollRegion}
				data-testid={"user-list-scroll-region"}
				onScroll={handleListScroll}
			>
				{loading ? (
					Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
						<LoadingUserCard key={`loading-${index}`} />
					))
				) : (
					<>
						{filteredUsers.length === 0 ? (
							<div className={styles.emptyMessage}>No matching users</div>
						) : null}
						{visibleUsers.map((user) => (
							<div key={user.userId}>
								<div className={styles.userCard}>
									<div className={`${styles.row} ${styles.cardHeader}`}>
										<div>Email</div>
										<div>Permission Level</div>
									</div>
									<div className={`${styles.row} ${styles.cardContent}`}>
										<div className={styles.email}>
											{user.email.split("@")[0]}@<wbr />
											{user.email.split("@").slice(1).join("@")}
										</div>
										<div>{user.role}</div>
									</div>
									<div className={styles.actions}>
										<Button
											disabled={
												getRoleId(currentUserRole) !== 1 &&
												getRoleId(currentUserRole) !== 4
											}
											onClick={() => {
												onEdit(user);
											}}
											svgIcon={edit_05}
											variant={"tertiary"}
										/>
										<Button
											disabled={
												getRoleId(currentUserRole) !== 1 &&
												getRoleId(currentUserRole) !== 4
											}
											onClick={() => {
												onDelete(user);
											}}
											svgIcon={trash_01}
											variant={"dangerTertiary"}
										/>
									</div>
								</div>
							</div>
						))}
					</>
				)}
			</div>
		</TraxCard>
	);
};
